import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Popper, Stack} from 'views/components';
import {AccessTime as AccessTimeIcon, Close} from 'views/icons';
import {translate} from 'views/module';

import PredefinedOptionsPane from './Panes/PredefinedOptionsPane';

import predefinedOptionsConfigs from './util/predefinedOptionsConfigs';
import CustomDatePane from './Panes/CustomDatePane';

import moment from 'moment';

const ReminderPicker = (props) => {
  const {
    dueDate,
    reminderDate,
    handleReminderDateChange,
  } = props;

  const [isReminderPickerOpen, setIsReminderPickerOpen] = React.useState(false);
  const [selectionTab, setSelectionTab] = React.useState('predefined');
  const selectedReminderConfig = React.useMemo(() => {
    if (!reminderDate) {
      return null;
    }

    return predefinedOptionsConfigs.find((config) => (
      reminderDate === config.converter(dueDate)
    )) || null;
  }, [dueDate, reminderDate]);

  const reminderDateLabel = React.useMemo(() => {
    if (selectedReminderConfig?.code) {
      return selectedReminderConfig.code;
    }

    if (reminderDate) {
      return moment(reminderDate).local().format('MMM D h:mmA');
    }

    return '';
  }, [reminderDate, selectedReminderConfig]);

  React.useEffect(() => {
    if (!selectedReminderConfig) {
      return;
    }

    const updatedReminderDate = selectedReminderConfig.converter(dueDate);

    if (updatedReminderDate !== reminderDate) {
      handleReminderDateChange(updatedReminderDate);
    }
  }, [dueDate, handleReminderDateChange, reminderDate, selectedReminderConfig]);

  const reminderButtonRef = React.useRef(null);

  const tabItems = [
    {value: 'predefined', label: translate('tasks.Common.ReminderPicker.PredefinedOptions.TabTitle')},
    {value: 'custom', label: translate('tasks.Common.ReminderPicker.Custom.TabTitle')},
  ];

  const handleSelectReminderDate = React.useCallback((nextReminderDate) => {
    handleReminderDateChange(nextReminderDate);
    setIsReminderPickerOpen(false);
  }, [handleReminderDateChange]);

  const handleClearReminderDate = React.useCallback((event) => {
    event.stopPropagation();
    handleReminderDateChange(null);
  }, [handleReminderDateChange]);

  return (
    <>
      <Box
        ref={reminderButtonRef}
        sx={{
          display: 'inline-flex',
          position: 'relative',
        }}
      >
        <Button
          variant='outlined'
          onClick={() => setIsReminderPickerOpen(true)}
          startIcon={<AccessTimeIcon sx={{fontSize: 16}} />}
          sx={{
            'borderColor': '#E8E4DE',
            'color': reminderDateLabel ? '#C768C7' : '#7A756E',
            'backgroundColor': 'transparent',
            'borderRadius': '8px',
            'textTransform': 'none',
            'minWidth': 0,
            'px': 1.5,
            'pr': reminderDate ? 4 : 1.5,
            'py': 0.5,
            '& .MuiButton-startIcon': {
              color: reminderDateLabel ? '#C768C7' : '#A8A49E',
            },
          }}
        >
          {reminderDateLabel ? reminderDateLabel : `${translate('tasks.Common.ReminderPicker.Reminders')}`}
        </Button>
        {
          reminderDate &&
          <Box
            component='button'
            type='button'
            onClick={handleClearReminderDate}
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              border: '1px solid #D5D1CB',
              backgroundColor: '#FFFFFF',
              color: '#7A756E',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0,
              cursor: 'pointer',
              position: 'absolute',
              top: '50%',
              right: 8,
              transform: 'translateY(-50%)',
            }}
          >
            <Close sx={{fontSize: 10}} />
          </Box>
        }
      </Box>
      <Popper
        open={isReminderPickerOpen}
        anchorEl={reminderButtonRef.current}
        placement='bottom-start'
        onClickAway={() => setIsReminderPickerOpen(false)}
        popperModifiers={[
          {
            name: 'flip',
            enabled: false,
          },
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
        zIndex={1400}
      >
        <Box
          sx={{
            mt: 1,
            width: 460,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 440,
            overflow: 'hidden',
            border: '1px solid #E8E4DE',
            borderRadius: '8px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 8px 24px rgba(25, 25, 25, 0.12)',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Stack direction='row' spacing={1} sx={{flexWrap: 'wrap'}}>
            {
              tabItems.map((tab) => (
                <Button
                  key={tab.value}
                  variant='outlined'
                  onClick={() => setSelectionTab(tab.value)}
                  sx={{
                    borderRadius: '999px',
                    borderColor: selectionTab === tab.value ? '#DA7955' : '#E8E4DE',
                    color: selectionTab === tab.value ? '#DA7955' : '#7A756E',
                    textTransform: 'none',
                    minWidth: 0,
                    px: 1.25,
                    py: 0.25,
                    fontSize: '13px',
                    lineHeight: 1.2,
                  }}
                >
                  {tab.label}
                </Button>
              ))
            }
          </Stack>
          {
            selectionTab === 'predefined' &&
            <PredefinedOptionsPane
              dueDate={dueDate}
              handleSelectReminderDate={handleSelectReminderDate}
            />
          }
          {
            selectionTab === 'custom' &&
            <CustomDatePane
              dueDate={dueDate}
              handleSelectReminderDate={handleSelectReminderDate}
            />
          }
        </Box>
      </Popper>
    </>
  );
};

ReminderPicker.propTypes = {
  dueDate: PropTypes.string.isRequired,
  reminderDate: PropTypes.string,
  handleReminderDateChange: PropTypes.func.isRequired,
};

export default React.memo(ReminderPicker);
